// @givmo/employer-module — Reports → the year recap (EP-WEB7; redesigned at EP-WEB26).
//
// The slide-ready summary of a company's giving year: what an HR lead takes into a leadership update
// and a CEO puts on an all-hands slide. It reads top to bottom as the story is told:
//
//   1. how much the company gave in total, how that compares with this point last year, and what the
//      total is made of — the two legs, employee donations matched and the company's own giving;
//   2. the program's reach — charities supported, employees enrolled, the median match;
//   3. where the money went and when — the most supported charities and the busiest month.
//
// **Every figure is a live read, and each one states its basis in plain words.** That is the design
// constraint (spec §103: "never render a figure the backend can't substantiate"), so the recap is
// assembled from the three dashboard reads that already exist rather than a bespoke aggregate:
//
//   - `GET /admin/company/dashboard`                 → the scalars (matched, company gave, employees, charities)
//   - `GET /admin/company/dashboard/top-charities`   → the ranked charity list
//   - `GET /admin/company/dashboard/donation-volume` → the monthly series (the busiest month)
//
// The basis lines are the ones the recap has always carried, word for word; what EP-WEB26 removed is
// the API route each one used to end with ("· GET /admin/company/dashboard"). A route is developer
// detail: it answers "which endpoint?", a question no one reading a recap asks, and it made every line
// read like a log. The routes stay here, in the code, where they are useful.
//
// **Periods are THIS YEAR and PRIOR YEAR-TO-DATE only, deliberately.** Those are the exact windows the
// dashboard reads serve. An arbitrary-year recap would need the annual-report assembly exposed as a
// synchronous read (a worker path, unmeasured) — so rather than offer a year selector that silently
// returns the wrong window, the recap states its period in the heading and stops there.
//
// **The matched/gave split is load-bearing here.** After EP-WEB-B1, `matched_ytd` is approved matches
// ONLY and `company_gave_ytd` is the company's own giving. The total is shown as their SUM, with both
// legs beside it and a bar drawing the proportion — the reconciliation is on the page, not implied.
// Money raised THROUGH the company's fundraisers is a different dimension (D-FCM-5: "raised" is never
// summed with "gave") and is not part of this recap; its placement is held (D1).

import { useId, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  companiesGetCompanyDashboardOptions,
  companiesGetCompanyDonationVolumeOptions,
  companiesGetCompanyTopCharitiesOptions,
} from '@givmo/api-client/query';
import { Avatar, Card, Icon, Skeleton, cn, testIdProp } from '@givmo/design-system';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { formatCount, formatUsd, yoyDelta } from '../dashboard/dashboard-decode';
import { InlineError, errorMessage } from '../dashboard/sections/section-status';

const PREFIX = 'recap';

/** How many charities the recap names before summarizing the remainder. */
const NAMED_CHARITIES = 5;

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** "2026-09" → "September"; the raw string when it is not a month period. */
function monthName(period: string): string {
  const match = /^\d{4}-(\d{2})$/.exec(period);
  return (match && MONTH_NAMES[Number(match[1]) - 1]) ?? period;
}

/** A number the read carried, or undefined — never a coerced string or NaN. */
function num(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** "$221,140 at this point last year", or nothing when the read serves no prior figure. */
function priorNote(value: number | undefined, format: (n: number) => string): string | undefined {
  return value === undefined ? undefined : `${format(value)} at this point last year`;
}

/** The year-over-year change as a small chip: up reads as growth, down stays neutral (not an error). */
function DeltaChip({ current, prior }: { current: number | undefined; prior: number | undefined }) {
  const delta = yoyDelta(current, prior);
  if (delta === null) return null;
  const up = delta.direction === 'up';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 rounded-md px-6 py-2 font-sans text-s12 font-w600 tracking-normal tabular-nums',
        up ? 'bg-success-50 text-success-700' : 'bg-neutral-100 text-neutral-600',
      )}
      {...testIdProp(`${PREFIX}-total-delta`)}
    >
      <Icon icon={up ? ArrowUpRight : ArrowDownRight} size={12} aria-hidden />
      {delta.text}
    </span>
  );
}

/** One leg of the total: its swatch, name and amount, then its basis and last year's figure. */
function LegRow({
  id,
  swatch,
  label,
  value,
  basis,
  comparison,
}: {
  id: string;
  swatch: string;
  label: string;
  value: string;
  basis: string;
  comparison?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-4" {...testIdProp(`${PREFIX}-figure-${id}`)}>
      <div className="flex items-baseline justify-between gap-12">
        <p className="flex min-w-0 items-center gap-8 font-sans text-s14 font-w600 tracking-givmo text-text-strong">
          <span className={cn('size-10 shrink-0 rounded-xs', swatch)} aria-hidden />
          {label}
        </p>
        <p className="shrink-0 font-sans text-s18 font-w700 tracking-givmo text-text-strong tabular-nums">
          {value}
        </p>
      </div>
      <div className="flex flex-wrap items-baseline justify-between gap-x-12 gap-y-2 pl-18">
        <p className="font-sans text-s12 tracking-normal text-text-tertiary">{basis}</p>
        {comparison ? (
          <p className="font-sans text-s12 tracking-normal text-text-tertiary tabular-nums sm:ml-auto">
            {comparison}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** One of the three reach figures. */
function ReachCell({
  id,
  label,
  value,
  basis,
  comparison,
}: {
  id: string;
  label: string;
  value: string;
  basis: string;
  comparison?: string;
}) {
  return (
    // A phone reads each figure as a row (name and basis left, figure and comparison right); from `sm`
    // the three stand side by side as cells.
    <div
      className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-12 gap-y-2 px-20 py-14 sm:flex sm:flex-col sm:gap-4 sm:px-24 sm:py-20"
      {...testIdProp(`${PREFIX}-figure-${id}`)}
    >
      <p className="col-start-1 row-start-1 font-sans text-s14 font-w600 tracking-givmo text-text-strong sm:text-s13 sm:font-w500 sm:tracking-normal sm:text-text-tertiary">
        {label}
      </p>
      <p className="col-start-2 row-start-1 text-right font-sans text-s18 font-w700 tracking-givmo text-text-strong tabular-nums sm:text-left sm:text-s24">
        {value}
      </p>
      <p className="col-start-1 row-start-2 font-sans text-s12 tracking-normal text-text-tertiary">
        {basis}
      </p>
      {comparison ? (
        <p className="col-start-2 row-start-2 text-right font-sans text-s12 tracking-normal text-text-tertiary tabular-nums sm:text-left">
          {comparison}
        </p>
      ) : null}
    </div>
  );
}

/** A labelled block inside the recap (the charities list, the busiest month). */
function Block({
  title,
  children,
  testId,
  className,
}: {
  title: string;
  children: ReactNode;
  testId?: string;
  className?: string;
}) {
  return (
    <div
      className={cn('flex min-w-0 flex-col gap-12 px-20 py-20 sm:px-24', className)}
      {...testIdProp(testId)}
    >
      <p className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">{title}</p>
      {children}
    </div>
  );
}

/**
 * The year so far, one bar per month, the busiest one in the brand colour. Every bar is a real
 * monthly total from the volume read, drawn in a track so an empty month reads as empty, never as a
 * short bar. Decorative to assistive technology — the month and its amount above are the statement.
 */
function MonthBars({
  months,
  peak,
}: {
  months: readonly { period: string; total: number }[];
  peak: string;
}) {
  const max = Math.max(...months.map((month) => month.total));
  const first = months[0];
  const last = months[months.length - 1];
  return (
    // Capped below `lg`, where the block runs the card's full width: nine bars across a tablet read as
    // slabs, not a year.
    <div
      className="flex w-full flex-col gap-6 max-lg:max-w-[420px]"
      aria-hidden
      {...testIdProp(`${PREFIX}-months`)}
    >
      <div className="flex h-[64px] items-end gap-4">
        {months.map((month) => (
          <span
            key={month.period}
            className="flex h-full min-w-0 flex-1 items-end overflow-hidden rounded-xs bg-neutral-100"
          >
            <span
              className={cn(
                'w-full rounded-xs',
                month.period === peak ? 'bg-brand-600' : 'bg-brand-200',
              )}
              style={{ height: `${max > 0 ? (month.total / max) * 100 : 0}%` }}
            />
          </span>
        ))}
      </div>
      <div className="flex justify-between font-sans text-s11 tracking-normal text-text-tertiary">
        <span>{first ? monthName(first.period).slice(0, 3) : ''}</span>
        <span>{last ? monthName(last.period).slice(0, 3) : ''}</span>
      </div>
    </div>
  );
}

function RecapSkeleton() {
  // Arbitrary heights, not scale keys — on this px-token codebase `h-40` would render 160px.
  return (
    <div className="flex flex-col gap-16 px-20 py-24 sm:px-24" {...testIdProp(`${PREFIX}-loading`)}>
      <Skeleton className="h-[14px] w-[120px]" />
      <Skeleton className="h-[36px] w-[220px]" />
      <Skeleton className="h-[10px] w-full rounded-full" />
      <div className="grid gap-16 sm:grid-cols-2">
        <Skeleton className="h-[44px] w-full" />
        <Skeleton className="h-[44px] w-full" />
      </div>
    </div>
  );
}

export function RecapSection() {
  const year = new Date().getFullYear();
  const headingId = useId();

  const dashboard = useQuery({ ...companiesGetCompanyDashboardOptions() });
  const topCharities = useQuery({
    ...companiesGetCompanyTopCharitiesOptions({ query: { limit: NAMED_CHARITIES } }),
  });
  const volume = useQuery({ ...companiesGetCompanyDonationVolumeOptions() });

  // Tolerant reads: an unexpected shape yields "—" per figure rather than breaking the section.
  const d = (dashboard.data ?? {}) as Record<string, unknown>;
  const matched = num(d.matched_ytd);
  const gave = num(d.company_gave_ytd);
  const matchedPrior = num(d.matched_prior_ytd);
  const gavePrior = num(d.company_gave_prior_ytd);
  // A sum of two ABSENT legs is unknown, not zero: coalescing each to 0 rendered "$0" beside two "—"
  // legs — a recap asserting the company gave nothing, the one statement here that could actually
  // mislead a board. `num` also keeps a non-number leaking through the cast from concatenating.
  const total = matched !== undefined && gave !== undefined ? matched + gave : undefined;
  const totalPrior =
    matchedPrior !== undefined && gavePrior !== undefined ? matchedPrior + gavePrior : undefined;
  const charitiesSupported = num(d.charities_supported_ytd);
  const charitiesPrior = num(d.charities_supported_prior_ytd);
  const employees = num(d.active_employees);
  const joined = num(d.active_employees_joined_this_year);
  const median = num(d.median_donation_match);

  /**
   * The year's months so far, each the sum of both legs, and the busiest of them — a deck-friendly
   * narrative beat. The read carries January through the CURRENT month on the backend's ET calendar;
   * the filter below keeps the page to months up to the viewer's own current month, so a month still
   * to come is never drawn or named (its zero would read as "nothing given", not "not yet").
   */
  const { months, peakMonth } = useMemo(() => {
    const series = (volume.data as { donation_volume_series?: unknown[] } | undefined)
      ?.donation_volume_series;
    const empty = { months: [] as { period: string; total: number }[], peakMonth: null };
    if (!Array.isArray(series) || series.length === 0) return empty;
    const now = new Date();
    const through = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const sofar: { period: string; total: number }[] = [];
    let best: { period: string; total: number } | null = null;
    for (const raw of series) {
      const point = raw as {
        period?: string;
        current_year?: number;
        current_year_company_gave?: number;
      };
      if (typeof point.period !== 'string') continue;
      // A month still to come is not a month of the year so far — not a bar, and never the busiest.
      if (/^\d{4}-\d{2}$/.test(point.period) && point.period > through) continue;
      const monthTotal = (point.current_year ?? 0) + (point.current_year_company_gave ?? 0);
      if (best === null || monthTotal > best.total)
        best = { period: point.period, total: monthTotal };
      if (/^\d{4}-\d{2}$/.test(point.period))
        sofar.push({ period: point.period, total: monthTotal });
    }
    return { months: sofar, peakMonth: best && best.total > 0 ? best : null };
  }, [volume.data]);

  const charities = useMemo(() => {
    const rows = (topCharities.data as { top_charities_ytd?: unknown[] } | undefined)
      ?.top_charities_ytd;
    if (!Array.isArray(rows)) return [];
    return rows.flatMap((raw) => {
      const row = raw as {
        charity?: { id?: string; name?: string; logo_url?: string | null };
        total?: number;
      };
      const name = row.charity?.name;
      return typeof name === 'string'
        ? [{ id: row.charity?.id, name, logo: row.charity?.logo_url ?? null, total: row.total }]
        : [];
    });
  }, [topCharities.data]);

  // The proportion bar needs two known, non-negative legs and something to divide.
  const share =
    matched !== undefined && gave !== undefined && matched >= 0 && gave >= 0 && matched + gave > 0
      ? (matched / (matched + gave)) * 100
      : null;
  const nothingYet = total === 0;
  // ══ LAST YEAR ONLY WHERE THERE WAS ONE (EP-WEB26, Jonathan's ruling 2026-09-29, Q3) ═══════════
  // The comparison — the growth chip, last year's total, and every "… at this point last year" line —
  // appears only when the company has giving ON RECORD by this point last year: one of last year's
  // to-date figures the dashboard read carries (matched, company gave, charities supported) is above
  // zero. A company that joined Givmo since then has no year to compare with, so its recap shows this
  // year alone rather than a column of "$0 at this point last year" — true words that read as "you
  // gave nothing". The rule reads the figures the recap already has; the company's creation date was
  // the other candidate, and it is the weaker one: a company can exist long before it first gives,
  // and the comparison is about giving.
  const hasPriorYear = [matchedPrior, gavePrior, charitiesPrior].some(
    (value) => value !== undefined && value > 0,
  );
  const compare = <T,>(value: T): T | undefined => (hasPriorYear ? value : undefined);

  // `id` + `tabIndex` make the section the target of the page's "View" tile and of the dashboard's
  // Export menu (`{ to: paths.reports, hash: 'recap' }`); `scroll-mt` keeps its heading clear of the
  // shell's sticky top bar when either lands here.
  return (
    <section
      id="recap"
      tabIndex={-1}
      aria-labelledby={headingId}
      className="scroll-mt-[88px] outline-none"
      {...testIdProp(`${PREFIX}-section`)}
    >
      <Card className="overflow-hidden">
        <div className="flex min-w-0 flex-col gap-4 border-b border-border-subtle px-20 py-16 sm:px-24 sm:py-20">
          <h2
            id={headingId}
            className="font-sans text-s18 font-w700 tracking-givmo text-text-strong"
          >
            {`${year} recap`}
          </h2>
          <p className="font-sans text-s14 tracking-normal text-text-tertiary">
            {hasPriorYear
              ? 'Your program so far this year, with last year to date for comparison.'
              : 'Your program so far this year.'}
          </p>
        </div>

        {dashboard.isPending ? (
          <RecapSkeleton />
        ) : dashboard.isError ? (
          // A failed read must not fall through to the figures: a recap that looks assembled, with
          // every number missing and no sign anything went wrong, reads as "we gave nothing".
          <div className="px-20 sm:px-24">
            <InlineError
              message={errorMessage(dashboard.error, 'Failed to load your recap.')}
              onRetry={() => void dashboard.refetch()}
            />
          </div>
        ) : (
          <>
            {/* 1 — the total, and what it is made of. */}
            <div className="flex flex-col gap-20 px-20 py-20 sm:px-24 sm:py-24">
              <div className="flex flex-col gap-4" {...testIdProp(`${PREFIX}-figure-total`)}>
                <p className="font-sans text-s13 font-w500 tracking-normal text-text-tertiary">
                  Total giving
                </p>
                <div className="flex flex-wrap items-center gap-x-10 gap-y-6">
                  <p className="font-sans text-s36 font-w700 leading-h1 tracking-givmo text-text-strong tabular-nums">
                    {formatUsd(total)}
                  </p>
                  {/* No growth chip on a year with nothing in it yet: "−100%" on January 2nd says
                      nothing the figures do not. Last year's total still shows beside it. */}
                  {hasPriorYear && !nothingYet ? (
                    <DeltaChip current={total} prior={totalPrior} />
                  ) : null}
                  {!hasPriorYear || totalPrior === undefined ? null : (
                    <p
                      className="font-sans text-s13 tracking-normal text-text-tertiary tabular-nums"
                      {...testIdProp(`${PREFIX}-total-prior`)}
                    >
                      {`vs ${priorNote(totalPrior, formatUsd)}`}
                    </p>
                  )}
                </div>
                <p className="font-sans text-s12 tracking-normal text-text-tertiary">
                  The sum of the two amounts below
                </p>
              </div>

              {share === null ? null : (
                <div
                  className="flex h-10 w-full overflow-hidden rounded-full bg-neutral-100"
                  aria-hidden
                  {...testIdProp(`${PREFIX}-composition`)}
                >
                  <span className="h-full bg-brand-600" style={{ width: `${share}%` }} />
                  <span className="h-full flex-1 bg-brand-300" />
                </div>
              )}

              {nothingYet ? (
                <p
                  className="font-sans text-s13 tracking-normal text-text-secondary"
                  {...testIdProp(`${PREFIX}-empty`)}
                >
                  Nothing has been given yet this year. This recap fills in as employee matches are
                  approved and your company gives.
                </p>
              ) : null}

              <div className="grid gap-16 sm:grid-cols-2 sm:gap-24">
                <LegRow
                  id="matched"
                  swatch="bg-brand-600"
                  label="Employee donations matched"
                  value={formatUsd(matched)}
                  // Naming the basis — EP-WEB-B1 made this approved-matches-only, and a figure that
                  // silently meant something wider is exactly what that change fixed.
                  basis="Approved employer matches"
                  comparison={compare(priorNote(matchedPrior, formatUsd))}
                />
                <LegRow
                  id="company-gave"
                  swatch="bg-brand-300"
                  label="Your company gave directly"
                  value={formatUsd(gave)}
                  basis="Direct gifts + card-funded gift cards"
                  comparison={compare(priorNote(gavePrior, formatUsd))}
                />
              </div>
            </div>

            {/* 2 — the program's reach. */}
            <div className="grid divide-y divide-border-subtle border-t border-border-subtle sm:grid-cols-3 sm:divide-x sm:divide-y-0">
              <ReachCell
                id="charities"
                label="Charities supported"
                value={formatCount(charitiesSupported)}
                basis="Distinct charities"
                comparison={compare(priorNote(charitiesPrior, formatCount))}
              />
              <ReachCell
                id="employees"
                label="Employees enrolled"
                value={formatCount(employees)}
                basis="Linked employees"
                comparison={
                  joined === undefined ? undefined : `${formatCount(joined)} joined this year`
                }
              />
              <ReachCell
                id="median"
                // "Median match" with a matches-only basis (D-EP-31): the ruling re-based the MATH
                // rather than retitling the figure, so the basis line names it.
                label="Median match (YTD)"
                value={formatUsd(median)}
                basis="Median of approved matches only"
              />
            </div>

            {/* 3 — where the money went, and when. */}
            <div className="grid border-t border-border-subtle lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
              <Block title="Most supported charities" testId={`${PREFIX}-charities`}>
                {/* Its own read: until it answers, and when it fails, the block says so. An
                    unanswered or failed read is not "no charity activity" — that statement belongs to
                    an answer that is empty. */}
                {topCharities.isPending ? (
                  <div
                    className="flex flex-col gap-10"
                    {...testIdProp(`${PREFIX}-charities-loading`)}
                  >
                    <Skeleton className="h-[20px] w-full" />
                    <Skeleton className="h-[20px] w-full" />
                    <Skeleton className="h-[20px] w-full" />
                  </div>
                ) : topCharities.isError ? (
                  <InlineError
                    message={errorMessage(topCharities.error, 'Failed to load top charities.')}
                    onRetry={() => void topCharities.refetch()}
                  />
                ) : charities.length === 0 ? (
                  <p className="font-sans text-s13 tracking-normal text-text-tertiary">
                    No charity activity yet this year.
                  </p>
                ) : (
                  <>
                    <ol className="flex flex-col">
                      {charities.map((charity, index) => (
                        <li
                          // Keyed on the charity id, not its name: two distinct charities can share
                          // a display name, and a duplicate key silently drops one row.
                          key={charity.id ?? `row-${index}`}
                          className="flex items-center gap-12 border-b border-border-subtle py-10 first:pt-0 last:border-b-0 last:pb-0"
                        >
                          <span className="w-12 shrink-0 font-sans text-s13 font-w600 tracking-givmo text-text-tertiary tabular-nums">
                            {index + 1}
                          </span>
                          <Avatar src={charity.logo} name={charity.name} size="xs" />
                          <span className="min-w-0 flex-1 truncate font-sans text-s14 font-w500 tracking-givmo text-text-strong">
                            {charity.name}
                          </span>
                          <span className="shrink-0 font-sans text-s14 font-w600 tracking-givmo text-text-strong tabular-nums">
                            {formatUsd(charity.total)}
                          </span>
                        </li>
                      ))}
                    </ol>
                    {/* Named, not "top N of M": the read is limited, so the recap does not claim the
                        full count. The uncapped list is the Impact export. */}
                    <p className="font-sans text-s12 tracking-normal text-text-tertiary">
                      The full list is in the Impact export.
                    </p>
                  </>
                )}
              </Block>

              {peakMonth === null ? null : (
                <Block
                  title="Busiest month"
                  testId={`${PREFIX}-peak-month`}
                  className="border-t border-border-subtle lg:border-t-0 lg:border-l"
                >
                  <div className="flex flex-col gap-4">
                    <p className="font-sans text-s24 font-w700 tracking-givmo text-text-strong">
                      {monthName(peakMonth.period)}
                    </p>
                    <p className="font-sans text-s13 tracking-normal text-text-tertiary tabular-nums">
                      {`${formatUsd(peakMonth.total)} given`}
                    </p>
                  </div>
                  {months.length > 1 ? <MonthBars months={months} peak={peakMonth.period} /> : null}
                </Block>
              )}
            </div>
          </>
        )}
      </Card>
    </section>
  );
}
